import React, { useMemo } from 'react';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getValueColor } from '../../utils/coloring';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import './Tables.css';

const HistoricalTrendsTable = ({ 
  selectedLocation, 
  selectedCrime, 
  selectedDateRange,
  setSelectedDateRange,
  setIsHistorical,
  selectedSource,
  compareMode
}) => {
  const { info, data } = selectedLocation;
  const locationType = info?.type?.charAt(0).toUpperCase() + info?.type?.slice(1);
  const isNation = info?.type?.toLowerCase() === 'nation';
  const isCounty = info?.type?.toLowerCase() === 'county';
  const isCity = info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = useMemo(() => {
    if (isCountyOrCity && info?.name?.includes(',')) {
      const stateName = info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, info?.name, selectedLocation]);

  const handleYearClick = (year) => {
    setSelectedDateRange(year.toString());
    setTimeout(() => {
      setIsHistorical(false);
    }, 100);
  };

  const transformData = () => {
    if (!data?.historical) {
      return [];
    }

    return data.historical
      .sort((a, b) => b.year - a.year)
      .map(yearData => {
        const crimeField = `${selectedCrime.toLowerCase()}_count`;
        const total = yearData[crimeField];
        const prevTotal = yearData[`${crimeField}_prev`];
        
        if (total === null || total === undefined) return null;

        const stateRankField = `${selectedCrime.toLowerCase()}_state_trend_rank`;
        const nationalRankField = `${selectedCrime.toLowerCase()}_national_trend_rank`;
        
        const rankField = isCountyOrCity
          ? compareMode === 'state'
            ? stateRankField
            : nationalRankField
          : `${selectedCrime.toLowerCase()}_trend_rank`;
        
        const statePercentileField = `${selectedCrime.toLowerCase()}_state_trend_percentile`;
        const nationalPercentileField = `${selectedCrime.toLowerCase()}_national_trend_percentile`;
        
        const percentileField = isCountyOrCity
          ? compareMode === 'state'
            ? statePercentileField
            : nationalPercentileField
          : `${selectedCrime.toLowerCase()}_trend_percentile`;

        return {
          year: yearData.year,
          total,
          prevTotal,
          difference: yearData[`${selectedCrime.toLowerCase()}_count_prev_diff`],
          percentDiff: yearData[`${selectedCrime.toLowerCase()}_count_prev_diff_pct`],
          stateRank: yearData[stateRankField],
          nationalRank: yearData[nationalRankField],
          statePercentile: yearData[statePercentileField],
          nationalPercentile: yearData[nationalPercentileField],
          rank: yearData[rankField],
          percentile: yearData[percentileField]
        };
      })
      .filter(row => row !== null);
  };

  const tableData = transformData();

  if (!tableData.length) {
    return <div className="loading">No Historical Data Available</div>;
  }

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
        {selectedCrime === 'all_crime' ? 'All' : ''}&nbsp;{getCrimeDisplayName(selectedCrime, selectedSource)}&nbsp;Historical Trends
      </div>
      <div className="main-table-scroll-container">
        <table className={`main-table ${isNation ? 'nation-view' : ''} trends-table historical-table`}>
          <thead>
            <tr>
              <th>Year</th>
              <th>
                <div className="stacked-header">
                  <div>Total</div>
                  <div>{getCrimeDisplayName(selectedCrime, selectedSource)}</div>
                </div>
              </th>
              <th>
                <div className="stacked-header">
                  <div>Previous</div>
                  <div>Total</div>
                </div>
              </th>
              <th>
                <div className="stacked-header">
                  <div>Change from</div>
                  <div>Previous Total</div>
                </div>
              </th>
              <th>
                <div className="stacked-header">
                  <div>% Change from</div>
                  <div>Previous Total</div>
                </div>
              </th>
              {!isNation && (
                <>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                      <div>Rank</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType}</div>
                      <div>Percentile</div>
                    </div>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row) => {
              const rankValue = isCountyOrCity && compareMode === 'state' ? 
                row.stateRank : 
                (isCountyOrCity ? row.nationalRank : row.rank);
                
              const percentileValue = isCountyOrCity && compareMode === 'state' ? 
                row.statePercentile : 
                (isCountyOrCity ? row.nationalPercentile : row.percentile);
              
              return (
                <tr 
                  key={row.year}
                  className={row.year === parseInt(selectedDateRange) ? 'selected-row' : ''}
                >
                  <td>
                    <span onClick={() => handleYearClick(row.year)}>
                      {row.year}
                    </span>
                  </td>
                  <td className="number-cell">
                    {formatNumber(row.total)}
                  </td>
                  <td className="number-cell">
                    {formatNumber(row.prevTotal)}
                  </td>
                  <td 
                    className="number-cell"
                    style={{ color: getValueColor(row.difference) }}
                  >
                    {formatDifference(row.difference)}
                  </td>
                  <td 
                    className="number-cell bold"
                    style={{ color: getValueColor(row.percentDiff) }}
                  >
                    {formatDifference(row.percentDiff, true)}
                  </td>
                  {!isNation && (
                    <>
                      <td className="rank-cell">{formatRank(rankValue)}</td>
                      <td className="rank-cell">{formatPercentile(percentileValue)}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HistoricalTrendsTable;